import { useMemo } from 'react';
import { useRouter } from 'next/router';
import { camelCase } from 'lodash-es';

import { Box, Flex, Radio, RadioGroup, Stack } from '@chakra-ui/react';

import { SelectInput } from '~/components/select-input';
import { trackEvent } from '~/features/analytics/track-event';
import { DEFAULT_SORT } from '~/features/catalog/models/filter';
import { updateCatalogUrlState } from '~/features/catalog/utils/catalog-url-state-utils';
import { getSortOptions } from '~/features/catalog/utils/get-sort-options';
import { useClientRender } from '~/hooks/use-client-render';
import { useI18n } from '~/hooks/use-i18n';
import { isSelectOption } from '~/types/select-option';

import { SortIcon } from '../icons';

export function CatalogSort() {
  const isClient = useClientRender();
  const router = useRouter();
  const { t } = useI18n();

  const searchValue = router.query.searchValue;

  const { selectedSortOption, sortOptions } = useMemo(() => {
    const sortOptions = getSortOptions(!!searchValue, t);
    const defaultSortOption = sortOptions.find((sortOption) => sortOption.value === DEFAULT_SORT);

    const { sort: sortParam } = router.query;

    return {
      selectedSortOption: sortOptions.find((sortOption) => sortOption.value === sortParam) ?? defaultSortOption,
      sortOptions,
    };
  }, [router.query, searchValue, t]);

  function handleSortChange(selectedOption: unknown) {
    if (isSelectOption(selectedOption)) {
      trackEvent({
        type: 'click',
        name: 'catalogResultSorted',
        label: selectedOption.label,
        action: 'results_sorted',
      });

      updateCatalogUrlState({
        router,
        updatedRouteParameters: {
          sort: selectedOption.value,
        },
      });
    }
  }

  function handleSortRadioChange(sortValue: (typeof sortOptions)[number]['value']) {
    const sortOptions = getSortOptions(!!searchValue, t);
    const selectedOption = sortOptions.find((sortOption) => sortOption.value === sortValue);

    handleSortChange(selectedOption);
  }

  return (
    <Box>
      <Box display={{ base: 'none', lg: 'block' }}>
        <Flex alignItems='center' as='label' gap='8px'>
          <Flex alignItems='center' color='grey.500' fontSize='0.8125rem' gap='6px' lineHeight='1.4'>
            <SortIcon height='16px' width='16px' />
            {t('catalog.sort.label')}
          </Flex>
          <SelectInput
            key={selectedSortOption?.value}
            name='catalogFilter'
            options={sortOptions}
            placeholder={selectedSortOption ? selectedSortOption.label ?? '' : ''}
            selectProps={{
              value: '',
              onChange: handleSortChange,
              size: 'sm',
              menuPortalTarget: isClient ? document.body : undefined,
            }}
          />
        </Flex>
      </Box>
      <Box display={{ base: 'block', lg: 'none' }}>
        <RadioGroup value={selectedSortOption?.value} onChange={handleSortRadioChange}>
          <Stack spacing='8px'>
            {sortOptions.map((sortOption) => (
              <Radio key={sortOption.value} size={{ base: 'lg', lg: 'md' }} value={sortOption.value}>
                <Box as='span' color='grey.500' fontSize='0.8125rem' lineHeight='1.4'>
                  {t(`catalog.sort.${camelCase(sortOption.value)}`)}
                </Box>
              </Radio>
            ))}
          </Stack>
        </RadioGroup>
      </Box>
    </Box>
  );
}
